import { AxiosError } from 'axios';
import { useEffect, useState, useRef } from 'react';
import { IdToken, useAuth0 } from '@auth0/auth0-react';
import { useMutation } from 'react-query';
import { Button } from '@tigergraph/app-ui-lib/button';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Navigation } from 'baseui/side-navigation';
import { HeadingMedium, Body1, Body2 } from '@tigergraph/app-ui-lib/typography';

import { logoutClearance } from '@/utils/utils.ts';

import { TermsConditions } from './TermsConditions';
import { PrivacyPolicy } from './PrivacyPolicy';
import { InlineBlock } from '@/components/landing/Style.tsx';
import { axiosOrgService } from '@/lib/network.ts';
import { useQueryParams } from '@/components/landing/hooks.ts';
import { Modal } from '@tigergraph/app-ui-lib/modal';
import { showToast } from '@/components/styledToasterContainer';
import { getCallbackURL } from '@/lib/auth.ts';
import { useNavigate } from 'react-router';
import { useOrgContext } from '@/contexts/orgContext';
import { useUserGuideContext } from '@/components/userguide/userGuideContext';
import { useMobile } from '@/contexts/mobileContext';
import mediaQuery from '@/lib/breakpoints';
import { OnboardingTaskName } from '@/components/userguide/utils';
import { BaseSkeleton } from '@/components/BaseSkeleton';
import { LoadingIndicator } from '@/components/loading-indicator';
import { usePageViewed } from '@/hooks/usePageViewed';
import trackUtil from '@/utils/analytics-service';

enum ItemKey {
  TermsConditions = 'termsConditions',
  PrivacyPolicy = 'privacyPolicy',
}

// Update this version when the terms and conditions are updated
const version: string = '1.0.1';
const initialVersion: string = '1.0.0';

export const isTermsAccepted = (idToken: IdToken | undefined) => {
  return !!(
    (idToken?.metadata?.accepted_tos && version === initialVersion) ||
    idToken?.metadata?.accepted_toc_version === version
  );
};

// if we need to show term again (isTermsAccepted return false)
// we need to check if the user has accepted the previous terms
export const isPreviousTermsAccepted = (idToken: IdToken | undefined) => {
  return !!idToken?.metadata?.accepted_tos;
};

export default function LegalTerms({
  isViewMode = false,
  showModal = true,
  onClose,
}: {
  isViewMode?: boolean;
  showModal?: boolean;
  onClose?: () => void;
}) {
  const { loginWithRedirect, logout } = useAuth0();
  const [css, theme] = useStyletron();
  const queryParams = useQueryParams();
  const [activeItem, setActiveItem] = useState<string>(ItemKey.TermsConditions);
  const { getIdTokenClaims } = useAuth0();
  const navigate = useNavigate();
  const [showUpdateDescription, setShowUpdateDescription] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const { rawOrg, userInfo } = useOrgContext();
  const { determineNeedWorkspaceMutation, completeOnboardingTask } = useUserGuideContext();
  const { isMobile } = useMobile();
  const overlayRef = useRef<HTMLDivElement>(null);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const orgFromQuery = queryParams.get('organization') || queryParams.get('org_id') || undefined;

  usePageViewed({
    page_name: 'terms_and_conditions',
    tab_selected: null,
    journey_name: 'account_creation',
    journey_step: 'legal_acceptance',
    organization_id: orgFromQuery,
    enabled: !isViewMode,
    // Organization may arrive from the URL before OrgContext finishes hydrating currentOrg.
    waitForOrg: false,
  });

  useEffect(() => {
    if (isViewMode) {
      return;
    }
    getIdTokenClaims().then((idToken) => {
      if (!isTermsAccepted(idToken)) {
        if (!isViewMode) {
          setShowUpdateDescription(isPreviousTermsAccepted(idToken));
        }
        return;
      }
    });
  }, [getIdTokenClaims, navigate, isViewMode]);

  // Manage focus when overlay is shown and cleanup timeout
  useEffect(() => {
    if (isProcessing || determineNeedWorkspaceMutation.isLoading) {
      // Focus the overlay container for screen readers
      overlayRef.current?.focus();
      // Store the previously focused element
      const previousActiveElement = document.activeElement as HTMLElement;
      // Prevent focus from reaching underlying controls
      const handleTabKey = (e: KeyboardEvent) => {
        if (e.key === 'Tab') {
          e.preventDefault();
          overlayRef.current?.focus();
        }
      };
      document.addEventListener('keydown', handleTabKey);
      return () => {
        document.removeEventListener('keydown', handleTabKey);
        // Clear redirect timeout when overlay is removed
        if (redirectTimeoutRef.current) {
          clearTimeout(redirectTimeoutRef.current);
          redirectTimeoutRef.current = null;
        }
        // Restore focus when overlay is removed
        if (previousActiveElement && document.contains(previousActiveElement)) {
          previousActiveElement.focus();
        }
      };
    } else {
      // Clear timeout when processing completes
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
        redirectTimeoutRef.current = null;
      }
    }
  }, [isProcessing, determineNeedWorkspaceMutation.isLoading]);

  async function updateToS() {
    try {
      const response = await axiosOrgService.patch('/users/me/metadata', {
        accepted_tos: true,
        accepted_toc_version: version,
      });

      return response.data;
    } catch (err: any) {
      return Promise.reject(err.response.data);
    }
  }

  const userMutation = useMutation(
    async () => {
      return await updateToS();
    },
    {
      onSuccess: (res) => {
        const org_id = queryParams.get('org_id') || queryParams.get('organization');
        if (res.user_metadata?.accepted_tos) {
          // Flushed with sendBeacon: this handler redirects through Auth0, so the page
          // unloads before a batched send would leave.
          if (userInfo?.id) {
            trackUtil.trackTermsAccepted({ user_id: userInfo.id, terms_version: version }, { flush: true });
          }
          // Set processing state to show skeleton overlay and prevent multiple clicks
          setIsProcessing(true);
          // complete onboarding task
          completeOnboardingTask(OnboardingTaskName.signup);

          const handleLoginRedirect = (goToWorkspaceSetup: boolean) => {
            try {
              if (org_id) {
                loginWithRedirect({
                  authorizationParams: {
                    'ext-origin': window.location.origin,
                    organization: org_id,
                    redirect_uri: getCallbackURL(
                      rawOrg.marketing_done
                        ? goToWorkspaceSetup
                          ? '/onboarding/setting-up-workspace'
                          : '/'
                        : '/get-started'
                    ),
                  },
                });
              } else {
                loginWithRedirect({
                  authorizationParams: {
                    'ext-origin': window.location.origin,
                    redirect_uri: getCallbackURL(
                      rawOrg.marketing_done
                        ? goToWorkspaceSetup
                          ? '/onboarding/setting-up-workspace'
                          : '/'
                        : '/get-started'
                    ),
                  },
                });
              }
              // Set a timeout to reset processing state if redirect doesn't occur
              // This is a safety mechanism in case loginWithRedirect fails silently
              // Clear any existing timeout first
              if (redirectTimeoutRef.current) {
                clearTimeout(redirectTimeoutRef.current);
              }
              redirectTimeoutRef.current = setTimeout(() => {
                setIsProcessing(false);
                redirectTimeoutRef.current = null;
              }, 5000);
            } catch (error) {
              // Reset processing state if redirect fails
              setIsProcessing(false);
              showToast({
                kind: 'negative',
                message: 'Failed to redirect. Please try again.',
              });
            }
          };

          determineNeedWorkspaceMutation.mutate(
            { acceptedTos: true },
            {
              onSuccess: (goToWorkspaceSetup) => {
                handleLoginRedirect(goToWorkspaceSetup);
              },
              onError: () => {
                setIsProcessing(false);
                handleLoginRedirect(false);
              },
            }
          );
        } else {
          setIsProcessing(false);
          showToast({
            kind: 'negative',
            message: 'Network Error: Please accept again.',
          });
        }
      },
      onError: (err: AxiosError) => {
        setIsProcessing(false);
        showToast({
          kind: 'negative',
          message: err?.message,
        });
      },
    }
  );

  return (
    <Modal
      animate={isViewMode}
      closeable={isViewMode}
      onClose={() => onClose && onClose()}
      isOpen={showModal}
      overrides={{
        Dialog: {
          style: {
            padding: '24px',
            marginLeft: 0,
            marginRight: 0,
            marginBottom: 0,
            marginTop: 0,
            width: isViewMode ? (isMobile ? '95vw' : '75vw') : '100vw',
            height: isViewMode ? (isMobile ? '90vh' : '85vh') : '100vh',
          },
        },
      }}
      autoFocus={false}
    >
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        })}
      >
        {/* Loading Overlay with Skeleton - Show only after API succeeds */}
        {(isProcessing || determineNeedWorkspaceMutation.isLoading) && (
          <div
            ref={overlayRef}
            role="status"
            aria-busy="true"
            aria-live="polite"
            aria-label="Processing your request"
            tabIndex={-1}
            className={css({
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: theme.colors['background.alternative'],
              zIndex: 1000,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '24px',
              outline: 'none',
            })}
          >
            <LoadingIndicator message="Processing your request..." />
            <div
              className={css({
                width: '100%',
                maxWidth: '600px',
                padding: '24px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              })}
            >
              <BaseSkeleton width="100%" height="24px" />
              <BaseSkeleton width="100%" height="16px" />
              <BaseSkeleton width="80%" height="16px" />
              <BaseSkeleton width="100%" height="16px" />
              <BaseSkeleton width="90%" height="16px" />
            </div>
          </div>
        )}
        {showUpdateDescription && (
          <div
            className={css({
              padding: '24px 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '8px',
            })}
          >
            <HeadingMedium>User Agreement Updated</HeadingMedium>
            <Body2>
              Please review and accept the updated Terms & Conditions and Privacy Policy to continue using our services.{' '}
            </Body2>
          </div>
        )}
        <InlineBlock
          className={css({
            flexGrow: 1,
            flexShrink: 1,
            flexBasis: 0,
            minHeight: 0,
            paddingRight: '24px',
            paddingLeft: '24px',
            paddingBottom: isViewMode ? '24px' : '0px',
            [mediaQuery.small]: {
              marginTop: isViewMode ? '24px' : '0px',
              display: 'flex',
              flexDirection: 'column',
              border: `1px solid ${theme.colors.divider}`,
              borderRadius: '2px',
              padding: '16px',
            },
          })}
        >
          <Navigation
            items={[
              {
                title: 'Terms & Conditions',
                itemId: ItemKey.TermsConditions,
              },
              {
                title: 'Privacy Policy',
                itemId: ItemKey.PrivacyPolicy,
              },
            ]}
            activeItemId={activeItem}
            onChange={({ event, item }) => {
              event.preventDefault();
              setActiveItem(item.itemId);
            }}
            overrides={{
              Root: {
                style: {
                  height: '100%',
                  borderRightColor: isMobile ? 'transparent' : theme.colors.divider,
                  borderRightWidth: '1px',
                  borderRightStyle: 'solid',
                  marginRight: isMobile ? '0px' : '22px',
                  paddingRight: isMobile ? '0px' : '24px',
                  flexShrink: 0,
                  [mediaQuery.small]: {
                    borderBottom: `1px solid ${theme.colors.divider}`,
                    height: 'auto',
                  },
                },
              },
              NavItem: {
                style: ({ $active }) => {
                  return {
                    backgroundColor: 'transparent',
                    borderLeftColor: 'transparent',
                    backgroundImage: 'none',
                    borderWidth: '0',
                    color: $active ? theme.colors['text.link'] : theme.colors['text.secondary'],
                    ':hover': {
                      color: theme.colors['text.link.pressed'],
                    },
                    fontFamily: 'Urbanist',
                    fontWeight: $active ? 600 : 500,
                    paddingTop: 0,
                    paddingRight: 0,
                    paddingBottom: '16px',
                    paddingLeft: 0,
                  };
                },
              },
            }}
          />
          {activeItem === ItemKey.TermsConditions && (
            <div
              className={css({
                [mediaQuery.small]: {
                  flexGrow: 1,
                  flexBasis: 0,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                },
              })}
            >
              <HeadingMedium as="div" marginBottom="8px">
                Terms & Conditions
              </HeadingMedium>
              {!isViewMode && (
                <Body1 color={theme.colors['text.secondary']} marginBottom="16px">
                  Please read these terms & conditions carefully, and accept to continue.
                </Body1>
              )}
              <div
                className={css({
                  height: `calc(100% - ${isViewMode ? 42 : 88}px)`,
                  overflow: 'auto',
                  [mediaQuery.small]: {
                    flexGrow: 1,
                    flexBasis: 0,
                    minHeight: 0,
                  },
                })}
              >
                <TermsConditions />
              </div>
            </div>
          )}

          {activeItem === ItemKey.PrivacyPolicy && (
            <div
              className={css({
                [mediaQuery.small]: {
                  flexGrow: 1,
                  flexBasis: 0,
                  minHeight: 0,
                },
              })}
            >
              <HeadingMedium as="div" marginBottom="8px">
                Privacy Policy
              </HeadingMedium>
              {!isViewMode && (
                <Body1 color={theme.colors['text.secondary']} marginBottom="16px">
                  Please read these privacy policy carefully, and accept to continue.
                </Body1>
              )}
              <div
                className={css({
                  height: `calc(100% - ${isViewMode ? 42 : 88}px)`,
                  overflow: 'auto',
                  [mediaQuery.small]: {
                    flexGrow: 1,
                    flexBasis: 0,
                    minHeight: 0,
                  },
                })}
              >
                <PrivacyPolicy />
              </div>
            </div>
          )}
        </InlineBlock>
        {!isViewMode && (
          <InlineBlock
            style={{
              gap: '16px',
              justifyContent: 'flex-end',
              paddingTop: '16px',
              paddingRight: '24px',
              paddingLeft: '24px',
              borderTop: `1px solid ${theme.colors.divider}`,
              marginTop: '16px',
            }}
          >
            <Button
              size="large"
              kind="secondary"
              disabled={userMutation.isLoading || isProcessing || determineNeedWorkspaceMutation.isLoading}
              onClick={() => {
                logoutClearance();
                logout({
                  logoutParams: { returnTo: getCallbackURL() },
                });
              }}
            >
              Decline
            </Button>
            <Button
              size="large"
              onClick={() => {
                userMutation.mutate();
              }}
              disabled={userMutation.isLoading || isProcessing || determineNeedWorkspaceMutation.isLoading}
              isLoading={userMutation.isLoading || isProcessing || determineNeedWorkspaceMutation.isLoading}
            >
              Accept
            </Button>
          </InlineBlock>
        )}

        {/*{hasError && (*/}
        {/*  <NegativeNotification*/}
        {/*    overrides={{*/}
        {/*      Body: {*/}
        {/*        style: {*/}
        {/*          position: 'absolute',*/}
        {/*        },*/}
        {/*      },*/}
        {/*    }}*/}
        {/*    onClose={closeError}*/}
        {/*  >*/}
        {/*    {errMsg}*/}
        {/*  </NegativeNotification>*/}
        {/*)}*/}
      </div>
    </Modal>
  );
}
